import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { API } from '../App';
import Navbar from '../components/Navbar';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { CheckCircle, XCircle, Users, Building2, MessageSquare, Star, Upload, Plus, Edit, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminDashboard() {
  const [analytics, setAnalytics] = useState(null);
  const [claimRequests, setClaimRequests] = useState([]);
  const [allBusinesses, setAllBusinesses] = useState([]);
  const [users, setUsers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addBusinessOpen, setAddBusinessOpen] = useState(false);
  const [editBusinessOpen, setEditBusinessOpen] = useState(false);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [uploadProgress, setUploadProgress] = useState('');
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    name: '',
    category_id: '',
    city_id: '',
    address: '',
    phone: '',
    email: '',
    website: '',
    description: '',
    opening_hours: '',
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [analyticsRes, claimsRes, businessesRes, usersRes, categoriesRes, citiesRes] = await Promise.all([
        axios.get(`${API}/admin/analytics`),
        axios.get(`${API}/admin/claim-requests`),
        axios.get(`${API}/admin/businesses`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/cities`),
      ]);
      setAnalytics(analyticsRes.data);
      setClaimRequests(claimsRes.data);
      setAllBusinesses(businessesRes.data);
      setUsers(usersRes.data);
      setCategories(categoriesRes.data);
      setCities(citiesRes.data);
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleApproveClaim = async (claimId) => {
    try {
      await axios.post(`${API}/admin/claim-requests/${claimId}/approve`);
      toast.success('Claim request approved!');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve claim');
    }
  };

  const handleRejectClaim = async (claimId) => {
    try {
      await axios.post(`${API}/admin/claim-requests/${claimId}/reject`);
      toast.success('Claim request rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject claim');
    }
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('file', file);

    setUploadProgress('Uploading...');
    try {
      const response = await axios.post(`${API}/admin/businesses/bulk-upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setUploadProgress('');
      toast.success(`Successfully uploaded ${response.data.created} businesses!`);
      if (response.data.errors.length > 0) {
        toast.warning(`${response.data.errors.length} rows had errors. Check console for details.`);
        console.log('Upload errors:', response.data.errors);
      }
      fetchData();
    } catch (error) {
      setUploadProgress('');
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
    
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddBusiness = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/businesses`, formData);
      toast.success('Business added successfully!');
      setAddBusinessOpen(false);
      setFormData({
        name: '',
        category_id: '',
        city_id: '',
        address: '',
        phone: '',
        email: '',
        website: '',
        description: '',
        opening_hours: '',
      });
      fetchData();
    } catch (error) {
      toast.error('Failed to add business');
    }
  };

  const handleEditBusiness = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/businesses/${selectedBusiness.id}`, formData);
      toast.success('Business updated successfully!');
      setEditBusinessOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update business');
    }
  };

  const handleDeleteBusiness = async (businessId) => {
    if (!window.confirm('Are you sure you want to delete this business?')) return;

    try {
      await axios.delete(`${API}/businesses/${businessId}`);
      toast.success('Business deleted successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete business');
    }
  };

  const openEditDialog = (business) => {
    setSelectedBusiness(business);
    setFormData({
      name: business.name,
      category_id: business.category_id,
      city_id: business.city_id,
      address: business.address,
      phone: business.phone,
      email: business.email || '',
      website: business.website || '',
      description: business.description,
      opening_hours: business.opening_hours || '',
    });
    setEditBusinessOpen(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const statsData = [
    { name: 'Businesses', value: analytics.total_businesses },
    { name: 'Users', value: analytics.total_users },
    { name: 'Leads', value: analytics.total_leads },
    { name: 'Reviews', value: analytics.total_reviews },
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <div className="container mx-auto px-4 md:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold" data-testid="admin-title">Admin Dashboard</h1>
          <p className="text-slate-600 mt-1">Manage platform operations and analytics</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList data-testid="admin-tabs">
            <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
            <TabsTrigger value="claims" data-testid="claims-tab">
              Claim Requests ({claimRequests.length})
            </TabsTrigger>
            <TabsTrigger value="businesses" data-testid="businesses-tab">Businesses</TabsTrigger>
            <TabsTrigger value="users" data-testid="users-tab">Users</TabsTrigger>
          </TabsList>

          <TabsContent value="overview" className="space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <Card className="p-6 shadow-sm" data-testid="stat-businesses">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Businesses</p>
                    <p className="text-3xl font-bold">{analytics.total_businesses}</p>
                    <p className="text-xs text-green-600 mt-1">
                      {analytics.claimed_businesses} claimed
                    </p>
                  </div>
                  <Building2 className="w-12 h-12 text-primary opacity-20" />
                </div>
              </Card>

              <Card className="p-6 shadow-sm" data-testid="stat-users">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Business Owners</p>
                    <p className="text-3xl font-bold">{analytics.total_users}</p>
                  </div>
                  <Users className="w-12 h-12 text-accent opacity-20" />
                </div>
              </Card>

              <Card className="p-6 shadow-sm" data-testid="stat-leads">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Leads</p>
                    <p className="text-3xl font-bold">{analytics.total_leads}</p>
                  </div>
                  <MessageSquare className="w-12 h-12 text-primary opacity-20" />
                </div>
              </Card>

              <Card className="p-6 shadow-sm" data-testid="stat-reviews">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Total Reviews</p>
                    <p className="text-3xl font-bold">{analytics.total_reviews}</p>
                  </div>
                  <Star className="w-12 h-12 text-yellow-400 opacity-20" />
                </div>
              </Card>
            </div>

            {/* Chart */}
            <Card className="p-6 shadow-sm">
              <h2 className="text-xl font-semibold mb-6">Platform Statistics</h2>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={statsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="name" stroke="#64748b" />
                  <YAxis stroke="#64748b" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: '#fff',
                      border: '1px solid #e2e8f0',
                      borderRadius: '8px',
                    }}
                  />
                  <Bar dataKey="value" fill="#064e3b" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </TabsContent>

          <TabsContent value="claims" className="space-y-6">
            {claimRequests.length === 0 ? (
              <Card className="p-12 text-center" data-testid="no-claims">
                <p className="text-slate-600">No pending claim requests</p>
              </Card>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {claimRequests.map((claim) => (
                  <Card key={claim.id} className="p-6 shadow-sm" data-testid={`claim-request-${claim.id}`}>
                    <h3 className="text-xl font-semibold mb-2">{claim.business_name}</h3>
                    <div className="space-y-2 text-sm mb-4">
                      <p><strong>Requested by:</strong> {claim.user_name}</p>
                      <p><strong>Email:</strong> {claim.user_email}</p>
                      <p><strong>Date:</strong> {new Date(claim.created_at).toLocaleDateString()}</p>
                    </div>

                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleApproveClaim(claim.id)}
                        className="flex-1 rounded-full bg-green-600 hover:bg-green-700 text-white"
                        data-testid={`approve-claim-${claim.id}`}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleRejectClaim(claim.id)}
                        variant="outline"
                        className="flex-1 rounded-full border-red-200 text-red-600 hover:bg-red-50"
                        data-testid={`reject-claim-${claim.id}`}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Reject
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="businesses">
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <Dialog open={addBusinessOpen} onOpenChange={setAddBusinessOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-full bg-primary hover:bg-primary/90" data-testid="add-business-button">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Business
                  </Button>
                </DialogTrigger>
                <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Add New Business</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleAddBusiness} className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Business Name *</Label>
                        <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
                      </div>
                      <div className="space-y-2">
                        <Label>Category *</Label>
                        <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select category" />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>City *</Label>
                        <Select value={formData.city_id} onValueChange={(value) => setFormData({ ...formData, city_id: value })}>
                          <SelectTrigger>
                            <SelectValue placeholder="Select city" />
                          </SelectTrigger>
                          <SelectContent>
                            {cities.map((city) => (
                              <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Phone *</Label>
                        <Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
                    </div>

                    <div className="space-y-2">
                      <Label>Address *</Label>
                      <Input required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
                    </div>

                    <div className="space-y-2">
                      <Label>Website</Label>
                      <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
                    </div>

                    <div className="space-y-2">
                      <Label>Opening Hours</Label>
                      <Input placeholder="e.g., Mon-Fri: 9AM-6PM" value={formData.opening_hours} onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })} />
                    </div>

                    <div className="space-y-2">
                      <Label>Description *</Label>
                      <Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
                    </div>

                    <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90">
                      Add Business
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>

              <div className="flex-1">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  onChange={handleBulkUpload}
                  className="hidden"
                  id="bulk-upload"
                />
                <label htmlFor="bulk-upload">
                  <Button
                    type="button"
                    variant="outline"
                    className="w-full sm:w-auto rounded-full"
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="bulk-upload-button"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {uploadProgress || 'Bulk Upload CSV/Excel'}
                  </Button>
                </label>
              </div>
            </div>

            <Card className="p-4 mb-6 bg-blue-50 border-blue-100">
              <p className="text-sm text-blue-900">
                <strong>CSV Format:</strong> Columns: name, category, city, address, phone, description, email (optional), website (optional), opening_hours (optional)
              </p>
            </Card>

            <Card className="shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="businesses-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Business</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Contact</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Status</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Rating</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {allBusinesses.map((business) => (
                      <tr key={business.id} data-testid={`business-row-${business.id}`}>
                        <td className="px-6 py-4">
                          <div>
                            <div className="font-medium text-slate-900">{business.name}</div>
                            <div className="text-sm text-slate-500">{business.address}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm">
                            <div>{business.email || '-'}</div>
                            <div className="text-slate-500">{business.phone}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${business.is_claimed ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'}`}>
                            {business.is_claimed ? 'Claimed' : 'Unclaimed'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400 mr-1" />
                            <span className="text-sm">{business.rating || 0}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => openEditDialog(business)}
                              data-testid={`edit-business-${business.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDeleteBusiness(business.id)}
                              data-testid={`delete-business-${business.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="users">
            <Card className="shadow-sm overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="users-table">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Name</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Email</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Role</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-slate-600 uppercase">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-200">
                    {users.map((user) => (
                      <tr key={user.id} data-testid={`user-row-${user.id}`}>
                        <td className="px-6 py-4">
                          <div className="font-medium text-slate-900">{user.name}</div>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">{user.email}</td>
                        <td className="px-6 py-4">
                          <span className={`px-2 py-1 text-xs rounded-full ${user.role === 'admin' ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'}`}>
                            {user.role}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-slate-600">
                          {new Date(user.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Edit Dialog */}
      <Dialog open={editBusinessOpen} onOpenChange={setEditBusinessOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Business</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditBusiness} className="space-y-4 mt-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Business Name *</Label>
                <Input required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category_id} onValueChange={(value) => setFormData({ ...formData, category_id: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>City *</Label>
                <Select value={formData.city_id} onValueChange={(value) => setFormData({ ...formData, city_id: value })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>{city.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Phone *</Label>
                <Input required value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>

            <div className="space-y-2">
              <Label>Address *</Label>
              <Input required value={formData.address} onChange={(e) => setFormData({ ...formData, address: e.target.value })} />
            </div>

            <div className="space-y-2">
              <Label>Website</Label>
              <Input value={formData.website} onChange={(e) => setFormData({ ...formData, website: e.target.value })} />
            </div>

            <div className="space-y-2">
              <Label>Opening Hours</Label>
              <Input value={formData.opening_hours} onChange={(e) => setFormData({ ...formData, opening_hours: e.target.value })} />
            </div>

            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea required rows={4} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </div>

            <Button type="submit" className="w-full rounded-full bg-primary hover:bg-primary/90">
              Update Business
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
